import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { usePageLoading } from "@/hooks/usePageLoading";
import CardWithImage from "@/components/elements/Card/CardThreeDImage";
import TitleWithContent from "@/components/elements/TitleWithContent/TitleWithContent";
import ButtonElement from "@/components/elements/Button/Button";
import { services as localServices } from "./ServicesArray";
import { fetchServiceCards } from "@/lib/queries";
import type { SanityImageData } from "@/types/sanity";

interface ServiceCard {
  linkUrl: string;
  cardTitle: string;
  cardContent: string;
  /** Bundled image URL (local services). */
  imageUrl?: string;
  /** Sanity image (CMS services). */
  image?: SanityImageData | null;
}

// Dynamically import all service images
const imageMap = import.meta.glob('@/assets/images/services/*', {
  eager: true,
  import: 'default',
});

// Resolve image by filename
const resolveImageUrl = (fileName: string): string => {
  const entry = Object.entries(imageMap).find(([path]) =>
    path.includes(fileName)
  );
  return entry ? (entry[1] as string) : '';
};

// Local services never change at runtime, so resolve their bundled images once.
const LOCAL_SERVICES: ServiceCard[] = localServices.map((service) => ({
  ...service,
  imageUrl: resolveImageUrl(service.imageFileName),
}));

const Services = ({ showLimited = false }) => {
  const { isLoading } = usePageLoading();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [sanityServices, setSanityServices] = useState<ServiceCard[]>([]);

  // Local services render immediately; CMS services are appended when they arrive.
  useEffect(() => {
    const controller = new AbortController();

    fetchServiceCards({ signal: controller.signal })
      .then((services) => {
        setSanityServices(
          services.map((service) => ({
            ...service,
            cardContent: service.cardContent ?? '',
          }))
        );
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        console.error("Failed to fetch Sanity services:", err);
      });

    return () => controller.abort();
  }, []);

  // Local entries win when a CMS entry points at the same URL.
  const combinedServices = useMemo(() => {
    const seen = new Set(LOCAL_SERVICES.map((service) => service.linkUrl));
    const extras = sanityServices.filter((service) => !seen.has(service.linkUrl));
    return [...LOCAL_SERVICES, ...extras];
  }, [sanityServices]);

  const displayedServices = showLimited
    ? combinedServices.slice(0, 6)
    : combinedServices;

  return (
    <div className="2xl:px-40 xl:px-35 lg:px-30 md:px-20 px-3 my-[5rem] ">
      <motion.div
        initial={{ opacity: 0, scaleX: 0 }}
        animate={!isLoading ? { opacity: 1, scaleX: 1 } : { opacity: 0, scaleX: 0 }}
        transition={{ duration: 0.5 }}
      >
        <TitleWithContent title="Services" level={showLimited ? 2 : 1} />
      </motion.div>

      <div className="2xl:px-40 xl:px-35 lg:px-30 md:px-20 px-5 transition-all duration-500">
        <motion.div
          className="grid gap-y-10 gap-x-6 sm:grid-cols-2  md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 place-items-center"
          initial={{ opacity: 0, x: -100 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1 }}
        >
          {displayedServices.map((service, index) => {
            const scale =
              hoveredIndex !== null
                ? hoveredIndex === index
                  ? 1.015
                  : 0.97
                : 1;

            return (
              <div
                key={service.linkUrl}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                className="w-full max-w-[280px] min-h-[300px] md:max-w-[330px] md:min-h-[380px]"
                style={{
                  transform: `scale(${scale})`,
                  transition:
                    "transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)",
                  boxShadow:
                    hoveredIndex === index
                      ? "0px 10px 20px rgba(0, 0, 0, 0.15)"
                      : "none",
                }}
              >
                <CardWithImage
                  linkUrl={service.linkUrl}
                  imageUrl={service.imageUrl}
                  image={service.image}
                  cardTitle={service.cardTitle}
                  cardContent={service.cardContent}
                />
              </div>
            );
          })}
        </motion.div>

        {showLimited && (
          <div className="mt-6 text-center">
            <ButtonElement
              btnText="See More Services"
              size="small"
              overrideTextColor={true}
              className="bg-transparent text-custom-secolor hover:text-white hover:bg-custom-secolor-dark px-6 py-2 rounded-sm"
              onClick={() => (window.location.href = "/services")}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Services;
